import type { ButtonHTMLAttributes, ReactNode } from "react";
import { CheckCircle2, Info, LoaderCircle, X, XCircle } from "lucide-react";
import type { ToastMessage } from "../types";
import { initials } from "../lib/format";

export function Button({
  tone = "primary",
  icon,
  children,
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  tone?: "primary" | "secondary" | "ghost" | "danger";
  icon?: ReactNode;
}) {
  return (
    <button className={`button button-${tone} ${className}`} {...props}>
      {icon}
      {children}
    </button>
  );
}

export function IconButton({
  label,
  children,
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  label: string;
}) {
  return (
    <button className={`icon-button ${className}`} aria-label={label} title={label} {...props}>
      {children}
    </button>
  );
}

export function Badge({
  tone = "neutral",
  children
}: {
  tone?: "neutral" | "success" | "warning" | "info" | "violet";
  children: ReactNode;
}) {
  return <span className={`badge badge-${tone}`}>{children}</span>;
}

export function Avatar({
  name,
  size = "md",
  tone = 0,
  imageUrl
}: {
  name: string;
  size?: "sm" | "md" | "lg";
  tone?: number;
  imageUrl?: string;
}) {
  return (
    <span className={`avatar avatar-${size} avatar-tone-${tone % 5}`} aria-hidden="true">
      {imageUrl ? <img src={imageUrl} alt="" /> : initials(name)}
    </span>
  );
}

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  wide = false,
  children
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  wide?: boolean;
  children: ReactNode;
}) {
  if (!open) return null;
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className={`modal ${wide ? "modal-wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="modal-header">
          <div>
            <h2>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          <IconButton label="Close" onClick={onClose}>
            <X size={19} />
          </IconButton>
        </header>
        <div className="modal-content">{children}</div>
      </section>
    </div>
  );
}

export function Loading({ label = "Loading" }: { label?: string }) {
  return (
    <div className="loading-state">
      <LoaderCircle size={22} className="spin" />
      <span>{label}</span>
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  message,
  action
}: {
  icon: ReactNode;
  title: string;
  message: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <span className="empty-icon">{icon}</span>
      <h3>{title}</h3>
      <p>{message}</p>
      {action}
    </div>
  );
}

export function Switch({
  checked,
  onChange,
  label
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      className={`switch ${checked ? "switch-on" : ""}`}
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
    >
      <span />
    </button>
  );
}

export function ToastStack({
  toasts,
  dismiss
}: {
  toasts: ToastMessage[];
  dismiss: (id: number) => void;
}) {
  return (
    <aside className="toast-stack" aria-live="polite">
      {toasts.map((toast) => (
        <div className={`toast toast-${toast.tone}`} key={toast.id}>
          {toast.tone === "success" ? (
            <CheckCircle2 size={19} />
          ) : toast.tone === "error" ? (
            <XCircle size={19} />
          ) : (
            <Info size={19} />
          )}
          <div>
            <strong>{toast.title}</strong>
            {toast.message && <p>{toast.message}</p>}
          </div>
          <IconButton label="Dismiss" onClick={() => dismiss(toast.id)}>
            <X size={15} />
          </IconButton>
        </div>
      ))}
    </aside>
  );
}
